import React, { useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";
import { useQuery, gql } from "@apollo/client";
import BookingWidget from "../components/BookingWidget";
import Stars from "../components/Stars";
import CriteriaBreakdown from "../components/CriteriaBreakdown";
import ProfileCard from "../components/ProfileCard";
import PhotoHero from "../components/PhotoHero";
import Lightbox from "../components/Lightbox";
import ApproximateMap from "../components/ApproximateMap";
import { blendedRating } from "../utils/reviewScore";
import { SAUNA_TYPE_LABELS } from "../utils/saunaTypes";
import { nameFor } from "../utils/displayName";

const GET_SAUNA = gql`
  query SaunaDetail($id: Int!) {
    saunaById(id: $id) {
      id name description addressLine city state postalCode country
      latitude longitude capacity pricePerHourCents isActive saunaType
      requiresApproval minBookingHours maxBookingHours
      saunaImagesBySaunaId(orderBy: DISPLAY_ORDER_ASC) {
        nodes { id url }
      }
      saunaAmenitiesBySaunaId {
        nodes {
          amenityByAmenityId { id label }
        }
      }
      userByOwnerId {
        id
        createdAt
        userProfileByUserId {
          fullName
          displayName
          hometown
          school
          work
          interests
          bio
        }
      }
      reviewsBySaunaId(condition: { reviewerRole: "guest" }, orderBy: CREATED_AT_DESC, first: 20) {
        nodes {
          id overall cleanliness communication accuracy valueScore body createdAt
          userByReviewerId { userProfileByUserId { fullName displayName } }
        }
      }
    }
  }
`;

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = React.useState(window.innerWidth >= 960);
  React.useEffect(() => {
    const handler = () => setIsDesktop(window.innerWidth >= 960);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isDesktop;
};

function Section({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <section style={{ borderTop: "1px solid #e5e7eb", padding: "32px 0" }}>
      {title && <h2 style={{ fontSize: 22, fontWeight: 600, margin: "0 0 20px" }}>{title}</h2>}
      {children}
    </section>
  );
}

export default function SaunaDetail() {
  const { id } = useParams<{ id: string }>();
  const { data, loading, error } = useQuery(GET_SAUNA, { variables: { id: parseInt(id!, 10) } });
  const sauna = data?.saunaById;
  usePageTitle(sauna ? `${sauna.name} — Lets Go Sauna` : "Lets Go Sauna");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const bookingRef = useRef<HTMLDivElement>(null);
  const isDesktop = useIsDesktop();

  if (loading) return <p style={{ padding: 24 }}>Loading...</p>;
  if (error) return <p style={{ padding: 24, color: "#ef4444" }}>Error: {error.message}</p>;

  const s = data?.saunaById;
  if (!s) return <p style={{ padding: 24 }}>Sauna not found.</p>;
  if (!s.isActive) return <p style={{ padding: 24 }}>This listing is not currently available.</p>;

  const images: { id: number; url: string }[] = s.saunaImagesBySaunaId?.nodes ?? [];
  const amenities: { id: number; label: string }[] = (s.saunaAmenitiesBySaunaId?.nodes ?? [])
    .map((n: any) => n.amenityByAmenityId)
    .filter(Boolean);
  const lat = s.latitude ? Number(s.latitude) : null;
  const lng = s.longitude ? Number(s.longitude) : null;
  const typeLabel = s.saunaType ? SAUNA_TYPE_LABELS[s.saunaType] ?? s.saunaType : null;

  const owner = s.userByOwnerId;
  const hostFirstName =
    owner?.userProfileByUserId?.displayName ||
    owner?.userProfileByUserId?.fullName?.split(" ")[0] ||
    "your host";

  const reviews: {
    id: number; overall: number;
    cleanliness?: number | null; communication?: number | null;
    accuracy?: number | null; valueScore?: number | null;
    body?: string | null; createdAt: string;
    userByReviewerId?: { userProfileByUserId?: { fullName?: string; displayName?: string | null } | null } | null;
  }[] = s.reviewsBySaunaId?.nodes ?? [];

  const avgRating = reviews.length > 0
    ? reviews.reduce((sum, r) => sum + blendedRating(r), 0) / reviews.length
    : null;

  const visibleReviews = showAllReviews ? reviews : reviews.slice(0, 6);

  function share() {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  }

  const bookingWidgetEl = (
    <BookingWidget
      saunaId={s.id}
      ownerId={owner?.id}
      pricePerHourCents={s.pricePerHourCents}
      requiresApproval={s.requiresApproval ?? false}
      minBookingHours={s.minBookingHours ?? 1}
      maxBookingHours={s.maxBookingHours ?? 8}
    />
  );

  const placeLine = [s.city, s.state, s.country].filter(Boolean).join(", ");

  return (
    <div style={{ maxWidth: 1280, margin: "32px auto", padding: isDesktop ? "0 32px 80px" : "0 20px 100px" }}>

      {/* Header */}
      <Link to="/" style={{ fontSize: 13, color: "#2563EB", textDecoration: "none" }}>← Back to listings</Link>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginTop: 16, gap: 16 }}>
        <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0, lineHeight: 1.2 }}>{s.name}</h1>
        <button onClick={share} style={shareBtn}>{copied ? "Copied!" : "⤴ Share"}</button>
      </div>
      <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, fontSize: 14, flexWrap: "wrap" }}>
        {avgRating !== null && (
          <>
            <Stars rating={avgRating} />
            <span style={{ fontWeight: 600 }}>{avgRating.toFixed(2)}</span>
            <span style={{ color: "#6b7280" }}>· {reviews.length} {reviews.length === 1 ? "review" : "reviews"}</span>
            <span style={{ color: "#d1d5db" }}>·</span>
          </>
        )}
        {placeLine && <span style={{ color: "#6b7280" }}>{placeLine}</span>}
      </div>

      {/* Photo hero — full width above the two-column body */}
      {images.length > 0 && (
        <div style={{ margin: "20px 0 36px" }}>
          <PhotoHero images={images} onOpen={setLightboxIndex} />
        </div>
      )}

      {/* Two-column body */}
      <div style={isDesktop
        ? { display: "grid", gridTemplateColumns: "1fr 380px", gap: "0 64px", alignItems: "start" }
        : {}
      }>

        {/* Left content column */}
        <div>

          {/* Listing summary */}
          <div style={{ paddingBottom: 28 }}>
            <h2 style={{ fontSize: 22, fontWeight: 600, margin: "0 0 6px" }}>
              {typeLabel ? `${typeLabel} sauna hosted by ${hostFirstName}` : `Hosted by ${hostFirstName}`}
            </h2>
            <p style={{ margin: 0, fontSize: 15, color: "#6b7280" }}>
              {s.capacity} {s.capacity === 1 ? "guest" : "guests"}
              {" · "}
              {s.minBookingHours ?? 1}–{s.maxBookingHours ?? 8} hr bookings
              {" · "}
              {s.requiresApproval ? "Host reviews requests" : "Instant book"}
            </p>
          </div>

          {/* Description */}
          {s.description && (
            <div style={{ borderTop: "1px solid #e5e7eb", padding: "32px 0" }}>
              <p style={{ margin: 0, fontSize: 16, lineHeight: 1.75, color: "#374151" }}>{s.description}</p>
            </div>
          )}

          {/* Amenities */}
          {amenities.length > 0 && (
            <Section title="What this place offers">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", rowGap: 14, columnGap: 16 }}>
                {amenities.map((a) => (
                  <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 15 }}>
                    <span style={{ fontSize: 16, color: "#111827", flexShrink: 0 }}>✓</span>
                    <span>{a.label}</span>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Location */}
          {lat !== null && lng !== null && (
            <Section title="Where you'll be">
              <ApproximateMap id={s.id} lat={lat} lng={lng} />
              {placeLine && (
                <p style={{ margin: "12px 0 2px", fontSize: 15, fontWeight: 500 }}>{placeLine}</p>
              )}
              <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>
                Exact location shared after your booking is confirmed.
              </p>
            </Section>
          )}

          {/* Reviews */}
          {reviews.length > 0 && (
            <Section title={`★ ${(avgRating ?? 0).toFixed(2)} · ${reviews.length} ${reviews.length === 1 ? "review" : "reviews"}`}>
              <CriteriaBreakdown reviews={reviews} role="guest" />
              <div style={{
                display: "grid",
                gridTemplateColumns: isDesktop ? "repeat(2, 1fr)" : "1fr",
                gap: 28,
                marginTop: 24,
              }}>
                {visibleReviews.map((r) => {
                  const reviewer = r.userByReviewerId;
                  const name = nameFor(reviewer?.userProfileByUserId, "Guest");
                  return (
                    <div key={r.id}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                        <div style={reviewAvatar}>{name[0].toUpperCase()}</div>
                        <div>
                          <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{name}</p>
                          <p style={{ margin: 0, fontSize: 12, color: "#9ca3af" }}>
                            {new Date(r.createdAt).toLocaleDateString("en-US", { month: "long", year: "numeric" })}
                          </p>
                        </div>
                      </div>
                      <Stars rating={blendedRating(r)} />
                      {r.body && (
                        <p style={{ margin: "6px 0 0", fontSize: 14, lineHeight: 1.6, color: "#374151" }}>{r.body}</p>
                      )}
                    </div>
                  );
                })}
              </div>
              {reviews.length > 6 && !showAllReviews && (
                <button onClick={() => setShowAllReviews(true)} style={showAllBtn}>
                  Show all {reviews.length} reviews
                </button>
              )}
            </Section>
          )}

          {/* Host */}
          {owner && (
            <div style={{ borderTop: "1px solid #e5e7eb", padding: "32px 0" }}>
              <ProfileCard user={owner} heading="Meet your host" sinceLabel="Host" />
            </div>
          )}

          {/* Mobile inline booking widget */}
          {!isDesktop && (
            <div ref={bookingRef} style={{ borderTop: "1px solid #e5e7eb", padding: "32px 0" }}>
              <h2 style={{ fontSize: 22, fontWeight: 600, margin: "0 0 20px" }}>Reserve</h2>
              {bookingWidgetEl}
            </div>
          )}
        </div>

        {/* Right column — sticky booking card (desktop only) */}
        {isDesktop && (
          <div style={{ position: "sticky", top: 24, alignSelf: "start" }}>
            {bookingWidgetEl}
          </div>
        )}
      </div>

      {/* Mobile bottom action bar */}
      {!isDesktop && (
        <div style={mobileBar}>
          <div>
            <span style={{ fontWeight: 700, fontSize: 18 }}>${(s.pricePerHourCents / 100).toFixed(2)}</span>
            <span style={{ fontSize: 14, color: "#6b7280" }}> / hr</span>
          </div>
          <button
            onClick={() => bookingRef.current?.scrollIntoView({ behavior: "smooth" })}
            style={reserveBtn}
          >
            Reserve
          </button>
        </div>
      )}

      {lightboxIndex !== null && (
        <Lightbox
          images={images}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />
      )}
    </div>
  );
}

const shareBtn: React.CSSProperties = {
  padding: "6px 14px", fontSize: 13, fontWeight: 500,
  background: "#fff", border: "1px solid #d1d5db", borderRadius: 8,
  cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
};

const reviewAvatar: React.CSSProperties = {
  width: 36, height: 36, borderRadius: "50%",
  background: "#e5e7eb", color: "#374151",
  display: "flex", alignItems: "center", justifyContent: "center",
  fontSize: 14, fontWeight: 700, flexShrink: 0,
};

const showAllBtn: React.CSSProperties = {
  marginTop: 24, padding: "10px 20px",
  background: "#fff", border: "1px solid #374151",
  borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer",
};

const mobileBar: React.CSSProperties = {
  position: "fixed", bottom: 0, left: 0, right: 0,
  background: "#fff", borderTop: "1px solid #e5e7eb",
  padding: "12px 20px 16px",
  display: "flex", justifyContent: "space-between", alignItems: "center",
  zIndex: 50,
};

const reserveBtn: React.CSSProperties = {
  padding: "12px 28px", background: "#2563EB", color: "#fff",
  border: "none", borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: "pointer",
};
